// The transparent silhouette variant, NOT footer.ts's opaque "econ-logo.png" — the source's
// own `.wv-econ-logo{filter:brightness(0)}` tint (below) needs a transparent glyph to tint,
// not a baked-in white rectangle; the source ships this pre-baked silhouette specifically for
// its own "waves"/"brand"/footer/network-side tinted wordmark slots (see `core/shell/chrome/
// logo-econ-mask.js`'s own doc comment — it swaps `#wavesEconLogo`'s `src` to this file at boot
// for exactly this reason, having previously tried a canvas-readback keying trick that broke
// under `file://`).
import econLogoUrl from "../skin/brand/econ-logo-silhouette.png?url";
import { currentMediaPreferences } from "../shell/media-preferences.js";
import { pathForApp } from "../runtime/stage-router.js";
import { mountWavesCanvas } from "./waves-canvas.js";
export const WAVES_STAGE_TAG = "node-waves-stage";
const APOSTROPHE = "’";
const HERO_WORD = `We${APOSTROPHE}re ready`;
const HERO_ARIA_LABEL = `We${APOSTROPHE}re ready.`;
const STEP_MS = 95;
const START_DELAY_MS = 320;
const APOSTROPHE_LULL_MS = 160;
const CARET_BLINK_MS = 2600;
/**
 * Epic #1507 fidelity F6 — the "waves" landing view, the original skin's real front door
 * (`.node-design/original-skin/index.html` lines ~504-523: `.wv-hero-title`/`.wv-search--
 * split`/`.wv-econ-logo`). Shell-owned like the 5 `SkinnedOwnerStageElement` subclasses (a
 * plain light-DOM custom element mounted directly by `app-shell.ts`, not a registry-installed
 * `StageLoader` app) but deliberately NOT one of them — it carries no kernel query, is not one
 * of the fixed `OWNER_STAGE_NAMES`, and is never reachable from `.pmenu`/`.tb-center` nav (the
 * source never lists it there either — it's the default view you land ON, not one you navigate
 * TO). `app-shell.ts` mounts it only for the router's own "no matching app" fallback path.
 *
 * Typing engine: ports `.node-design/original-skin/core/shell/router/index.js` lines 24-85
 * verbatim (char-by-char reveal, ~95ms/step + jitter, a longer pause after the apostrophe,
 * `prefers-reduced-motion` instant fast-path, then the coral caret blinks for 2.6s and
 * `.wv-search--split` fades in) — DOM-attribute-driven in the source (`data-type-text` read off
 * `#wvHeroTitle`), a single owned constant here since this stage is the only caller.
 *
 * Scheduling: driven by `requestAnimationFrame` rather than a self-re-arming `setTimeout`
 * chain — `client/ui` may not reach `client/kernel`'s `KernelScheduler` (boundary rule B2: UI
 * imports only `@econ-v1/rpc`), and that scheduler's ISO-`dueAt`/`cadenceMs` contract is
 * shaped for composition-root background jobs (sync, outbox replay), not a per-character view
 * animation. Each frame checks a `dueAt` timestamp computed with the exact same jitter/lull
 * formula the original `setTimeout` chain used, so the visual cadence is unchanged; only the
 * re-arm mechanism moved off ad-hoc timers (boundary rule C1), following the same
 * `requestAnimationFrame`-loop convention `waves-canvas.ts` already uses for its background.
 *
 * Background: `waves-canvas.ts`'s small CANVAS 2D dot-lattice — see that module's own doc
 * comment for why it is a deliberate `VANTA.DOTS` *approximation*, not a port.
 *
 * Submit: dispatches the same `navigate` custom event every other shell surface uses
 * (`wallet-status.ts`, `app-shell.ts`'s own `root.addEventListener("navigate", …)`) to the
 * Agents stage. The source's own prompt actually drives an agent conversation; wiring a real
 * agent hand-off is out of scope here — first pass is "submitting takes you to Agents",
 * matching the task's own "prefill acceptable first pass" note.
 */
export class NodeWavesStageElement extends HTMLElement {
    #canvas;
    #rafId;
    #blinkTimer;
    connectedCallback() {
        this.innerHTML = `<div class="wv-stage" data-waves-stage><canvas id="wvCanvas"></canvas><div class="wv-hud"><div class="wv-hero"><h1 class="wv-hero-title" data-waves-title aria-label="${HERO_ARIA_LABEL}"><span class="wv-type" data-waves-type aria-hidden="true"></span><span class="period">.</span><span class="wv-caret" aria-hidden="true"></span></h1><form class="wv-search wv-search--split" data-waves-search autocomplete="off"><span class="wv-search-pill"><input data-waves-input type="text" placeholder="Ask your agents anything&hellip;" maxlength="200"></span><button type="submit" aria-label="Ask"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="5.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 8 H13 M9 3.5 L13.5 8 L9 12.5"/></svg></button></form></div></div><img class="wv-econ-logo" src="${econLogoUrl}" alt="Economy V1"></div>`;
        const canvas = this.querySelector("#wvCanvas");
        if (canvas) {
            this.#canvas = mountWavesCanvas(canvas);
        }
        this.querySelector("[data-waves-search]")?.addEventListener("submit", this.#handleSubmit);
        this.#typeHero();
    }
    disconnectedCallback() {
        if (this.#rafId !== undefined) {
            cancelAnimationFrame(this.#rafId);
            this.#rafId = undefined;
        }
        clearTimeout(this.#blinkTimer);
        this.#canvas?.dispose();
        this.#canvas = undefined;
    }
    #handleSubmit = (event) => {
        event.preventDefault();
        this.dispatchEvent(new CustomEvent("navigate", {
            bubbles: true,
            composed: true,
            detail: { path: pathForApp("agents") },
        }));
    };
    #typeHero() {
        const title = this.querySelector("[data-waves-title]");
        const typeEl = this.querySelector("[data-waves-type]");
        if (!title || !typeEl) {
            return;
        }
        const reduced = currentMediaPreferences().prefersReducedMotion();
        const finish = () => {
            typeEl.textContent = HERO_WORD;
            title.classList.remove("typing");
            title.classList.add("type-done", "period-typed", "caret-blink");
            this.querySelector("[data-waves-search]")?.classList.add("wv-typed-done");
            this.#blinkTimer = setTimeout(() => title.classList.remove("caret-blink"), CARET_BLINK_MS);
        };
        if (reduced) {
            finish();
            return;
        }
        typeEl.textContent = "";
        title.classList.add("typing");
        let index = 0;
        let periodPending = false;
        let dueAt = performance.now() + START_DELAY_MS;
        // A single `requestAnimationFrame` loop that re-arms itself against a `dueAt` timestamp,
        // rather than a chain of self-re-arming `setTimeout` calls — see the class doc comment.
        const step = (now) => {
            if (now < dueAt) {
                this.#rafId = requestAnimationFrame(step);
                return;
            }
            if (periodPending) {
                title.classList.add("period-typed");
                finish();
                return;
            }
            typeEl.textContent = HERO_WORD.slice(0, index);
            if (index >= HERO_WORD.length) {
                periodPending = true;
                dueAt = now + STEP_MS;
                this.#rafId = requestAnimationFrame(step);
                return;
            }
            index++;
            const jitter = STEP_MS + (Math.random() * 50 - 18);
            const lull = HERO_WORD[index - 1] === APOSTROPHE ? APOSTROPHE_LULL_MS : 0;
            dueAt = now + jitter + lull;
            this.#rafId = requestAnimationFrame(step);
        };
        this.#rafId = requestAnimationFrame(step);
    }
}
export function registerWavesStageElement() {
    if (!customElements.get(WAVES_STAGE_TAG)) {
        customElements.define(WAVES_STAGE_TAG, NodeWavesStageElement);
    }
}
//# sourceMappingURL=waves-stage.js.map